import React from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Attachments } from "@/components/Attachments";
import { fmtMoney } from "@/lib/apiClient";

/**
 * PurchaseRowExpanded — extracted from Ledger.jsx (iter49 refactor).
 * Renders the expanded panel below a Purchase row showing:
 *   • Per-line-item table (Description · HSN · Qty · Unit · Rate · GST% · Line ₹)
 *   • Subtotal + CGST/SGST or IGST breakup + Grand Total
 *   • Attachments panel for the original bill PDF/image
 *
 * Props
 *   • row — the purchase document
 */
export default function PurchaseRowExpanded({ row }) {
  const r = row;
  return (
    <TableRow className="bg-slate-50">
      <TableCell colSpan={10} className="p-4">
        <div className="grid md:grid-cols-2 gap-6">
          {Array.isArray(r.items) && r.items.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Line Items</div>
              <table className="w-full text-[12px]">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="p-1.5 text-left font-semibold">Item</th>
                    <th className="p-1.5 text-left font-semibold">HSN</th>
                    <th className="p-1.5 text-right font-semibold">Qty</th>
                    <th className="p-1.5 text-left font-semibold">Unit</th>
                    <th className="p-1.5 text-right font-semibold">Rate</th>
                    <th className="p-1.5 text-right font-semibold">GST%</th>
                    <th className="p-1.5 text-right font-semibold">Line ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {r.items.map((it, i) => {
                    const line = Number(it.qty || 0) * Number(it.unit_price || 0);
                    return (
                      <tr key={`li-${r.id}-${i}-${it.description || it.sku || ""}`} className="border-t border-slate-200">
                        <td className="p-1.5">{it.description}</td>
                        <td className="p-1.5 font-mono text-[11px] text-slate-600">{it.hsn || "—"}</td>
                        <td className="p-1.5 text-right font-mono-num">{Number(it.qty || 0)}</td>
                        <td className="p-1.5 text-[11px] text-slate-600 uppercase tracking-wider">{it.unit || "nos"}</td>
                        <td className="p-1.5 text-right font-mono-num">{fmtMoney(it.unit_price)}</td>
                        <td className="p-1.5 text-right font-mono-num">{Number(it.gst_rate || 0)}%</td>
                        <td className="p-1.5 text-right font-mono-num font-semibold">{fmtMoney(line)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-3 text-[12px] font-mono-num text-slate-700 space-y-0.5">
                <div className="flex justify-between"><span>Subtotal</span><span>{fmtMoney(r.subtotal ?? 0)}</span></div>
                {(r.igst_amount || 0) > 0 ? (
                  <div className="flex justify-between"><span>IGST</span><span>{fmtMoney(r.igst_amount)}</span></div>
                ) : (
                  <>
                    {(r.cgst_amount || 0) > 0 && <div className="flex justify-between"><span>CGST</span><span>{fmtMoney(r.cgst_amount)}</span></div>}
                    {(r.sgst_amount || 0) > 0 && <div className="flex justify-between"><span>SGST</span><span>{fmtMoney(r.sgst_amount)}</span></div>}
                  </>
                )}
                <div className="flex justify-between font-semibold text-[13px] pt-1 border-t border-slate-300"><span>Grand Total</span><span className="text-red-700">{fmtMoney(r.total)}</span></div>
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Attached Bill</div>
            <Attachments parentType="purchase" parentId={r.id} />
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
}
